import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import GridLayout, { type Layout, type LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { WidgetRenderer } from './WidgetRenderer';
import type { ChartType, DashboardResponse } from '../../../types/analytics';

const GRID_COLUMNS = 12;
const GRID_ROW_HEIGHT = 40;
const GRID_MARGIN: readonly [number, number] = [12, 12];
const GRID_COLUMN_WIDTH = 84;
const GRID_FIT_WIDTH = (GRID_COLUMNS * GRID_COLUMN_WIDTH) + ((GRID_COLUMNS - 1) * GRID_MARGIN[0]);
const GRID_MIN_WIDTH = 720;

interface Props {
  dashboard: DashboardResponse;
  editable: boolean;
  onLayoutChange: (layout: LayoutItem[]) => void;
  onRemoveWidget: (widgetId: string) => void;
}

// Grid is 12 columns, rowHeight 40px. Default sizes are chosen so each widget
// type has enough room to render uncompressed; users can still resize freely
// down to the per-type minimums.
const SIZE_BY_CHART: Record<ChartType, { w: number; h: number; minW: number; minH: number }> = {
  kpi: { w: 3, h: 3, minW: 2, minH: 2 },
  pie: { w: 4, h: 8, minW: 3, minH: 4 },
  bar: { w: 6, h: 8, minW: 3, minH: 4 },
  line: { w: 6, h: 8, minW: 3, minH: 4 },
  area: { w: 6, h: 8, minW: 3, minH: 4 },
};

function sizeFor(chartType: ChartType) {
  return SIZE_BY_CHART[chartType] ?? SIZE_BY_CHART.bar;
}

/**
 * Build a complete layout covering every widget. Saved positions win; any widget
 * without a saved entry (just added, or after switching dashboards) gets a
 * chart-type-appropriate default, flow-placed left-to-right. This prevents
 * react-grid-layout from falling back to its tiny 1x1 default for those widgets.
 */
function buildLayout(dashboard: DashboardResponse): LayoutItem[] {
  const saved = new Map((dashboard.layout ?? []).map((l) => [l.i, l]));
  const repaired = new Map<string, LayoutItem>();
  let maxSavedBottom = 0;

  dashboard.widgets.forEach((widget) => {
    const s = sizeFor(widget.spec.chart_type);
    const existing = saved.get(widget.id);
    if (!existing) return;

    const item = {
      ...existing,
      w: Math.max(existing.w, s.minW),
      h: Math.max(existing.h, s.minH),
      minW: s.minW,
      minH: s.minH,
    };
    repaired.set(widget.id, item);
    maxSavedBottom = Math.max(maxSavedBottom, item.y + item.h);
  });

  let x = 0;
  let y = maxSavedBottom;
  let rowHeight = 0;
  return dashboard.widgets.map((widget) => {
    const s = sizeFor(widget.spec.chart_type);
    const existing = repaired.get(widget.id);
    if (existing) {
      return existing;
    }
    if (x + s.w > 12) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    const item: LayoutItem = { i: widget.id, x, y, w: s.w, h: s.h, minW: s.minW, minH: s.minH };
    x += s.w;
    rowHeight = Math.max(rowHeight, s.h);
    return item;
  });
}

export function DashboardGrid({ dashboard, editable, onLayoutChange, onRemoveWidget }: Props) {
  const [layout, setLayout] = useState<Layout>(() => buildLayout(dashboard));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(GRID_FIT_WIDTH);

  // Rebuild the layout when the dashboard or its widget set changes (switching
  // dashboards, or adding/removing widgets) — but NOT on plain layout saves, so
  // an in-progress drag/resize isn't reset under the user.
  const widgetSignature = useMemo(
    () => dashboard.widgets.map((w) => w.id).join(','),
    [dashboard.widgets],
  );
  useEffect(() => {
    setLayout(buildLayout(dashboard));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard.id, widgetSignature]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = (nextWidth: number) => {
      const next = Math.max(GRID_MIN_WIDTH, Math.floor(nextWidth));
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };

    measure(el.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') {
      const onResize = () => measure(el.getBoundingClientRect().width);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    let raf = 0;
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        measure(entry?.contentRect.width ?? el.getBoundingClientRect().width);
      });
    });

    observer.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  const handleChange = (next: Layout) => {
    setLayout(next);
    if (!editable) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onLayoutChange([...next]), 600);
  };

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <GridLayout className="layout" layout={layout} width={width}
        gridConfig={{ cols: GRID_COLUMNS, rowHeight: GRID_ROW_HEIGHT, margin: GRID_MARGIN }}
        dragConfig={{ enabled: editable, cancel: '.widget-remove' }}
        resizeConfig={{ enabled: editable }}
        onLayoutChange={handleChange}>
        {dashboard.widgets.map((w) => (
          <div key={w.id} className="relative h-full">
            {editable && (
              <button className="widget-remove absolute right-1 top-1 z-10 rounded bg-gray-100 dark:bg-gray-700 px-1 text-xs"
                onClick={() => onRemoveWidget(w.id)} aria-label={`Remove ${w.title}`}>✕</button>
            )}
            <WidgetRenderer title={w.title} spec={w.spec} />
          </div>
        ))}
      </GridLayout>
    </div>
  );
}
